import { invoke } from "@tauri-apps/api/core";
import { isPermissionGranted, requestPermission } from "@tauri-apps/plugin-notification";
import type { ActivityMode } from "../src/domain";

export interface NativeStatus {
  native: boolean;
  collecting: boolean;
  inputMonitoring: "granted" | "denied" | "unknown";
  appName?: string;
  bundleId?: string;
  presence: "engaged" | "attending" | "away" | "unknown";
  idleSeconds?: number;
  databasePath?: string;
  error?: string;
}

export interface ActivitySample {
  id: number;
  bucketStartMs: number;
  bucketEndMs: number;
  appName: string;
  bundleId: string;
  keystrokes: number;
  clicks: number;
  scrolls: number;
  mouseMoves: number;
  idleSeconds: number;
  presence: NativeStatus["presence"];
}

export interface NativeReminderContext {
  timerId: string;
  description: string;
  startedAtMs: number;
  nextEligibleAtMs: number;
  activityMode: ActivityMode;
  includeTaskName: boolean;
  quietStartMinutes: number;
  quietEndMinutes: number;
  timezoneOffsetMinutes: number;
}

export function isNative(): boolean {
  return "__TAURI_INTERNALS__" in window;
}

export async function loadNativeStatus(): Promise<NativeStatus> {
  if (!isNative()) return { native: false, collecting: false, inputMonitoring: "unknown", presence: "unknown" };
  return invoke<NativeStatus>("activity_status");
}

export async function loadNativeActivity(days = 7): Promise<ActivitySample[]> {
  if (!isNative()) return [];
  return invoke<ActivitySample[]>("activity_recent", { days });
}

export async function requestInputMonitoring(): Promise<NativeStatus> {
  if (!isNative()) return loadNativeStatus();
  return invoke<NativeStatus>("request_input_monitoring");
}

export async function setCollectionEnabled(enabled: boolean): Promise<NativeStatus> {
  if (!isNative()) return loadNativeStatus();
  return invoke<NativeStatus>("set_collection_enabled", { enabled });
}

export async function syncNativeReminder(context: NativeReminderContext | null): Promise<void> {
  if (!isNative()) return;
  await invoke("sync_reminder", { context });
}

export async function openDeviceAuthorization(requestId: string): Promise<void> {
  if (!isNative()) throw new Error("Browser approval is available from the Time Tile Mac app.");
  await invoke("open_device_authorization", { requestId });
}

export async function nativeNotificationPermission(request = false): Promise<"granted" | "denied" | "unavailable"> {
  if (!isNative()) return "unavailable";
  if (await isPermissionGranted()) return "granted";
  if (!request) return "denied";
  return await requestPermission() === "granted" ? "granted" : "denied";
}

declare global {
  interface Window { __TAURI_INTERNALS__?: unknown }
}
